import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Button, Modal, Form, Input, Card, Row, Col } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  PhoneOutlined, MailOutlined, EnvironmentOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import BASEURL from '../API/Config';

const { TextArea } = Input;

const Contact = () => {
  const [contactList, setContactList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [existingId, setExistingId] = useState(null);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${BASEURL}/master/getcontact`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setContactList(Array.isArray(data) ? data : []);
    } catch { Swal.fire('Error', 'Failed to load contact information.', 'error'); }
  };

  useEffect(() => { fetchContacts(); }, []);

  const openModal = (contact = null) => {
    if (contact) {
      setIsEditing(true); setExistingId(contact._id);
      form.setFieldsValue({ address: contact.addres, number: contact.number, email: contact.email, time: contact.time });
    } else {
      setIsEditing(false); setExistingId(null); form.resetFields();
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      const url = isEditing ? `${BASEURL}/master/updatecontact/${existingId}` : `${BASEURL}/master/createcontact`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ addres: values.address, number: values.number, email: values.email, time: values.time }),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Success', `Contact ${isEditing ? 'updated' : 'saved'}!`, 'success');
      setShowModal(false); fetchContacts();
    } catch (err) { Swal.fire('Error', err.message || 'Something went wrong.', 'error'); }
  };

  const handleDelete = (id) => {
    Swal.fire({ title: 'Delete Contact?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete' })
      .then(async (r) => {
        if (r.isConfirmed) {
          await fetch(`${BASEURL}/master/deletecontactus/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
          Swal.fire('Deleted!', 'Removed.', 'success'); fetchContacts();
        }
      });
  };

  const infoItems = contactList.length > 0 ? [
    { icon: <EnvironmentOutlined />, label: 'Address', value: contactList[0].addres, color: '#2874f0', bg: '#e8f1ff' },
    { icon: <PhoneOutlined />, label: 'Phone', value: contactList[0].number, color: '#059669', bg: '#ecfdf5' },
    { icon: <MailOutlined />, label: 'Email', value: contactList[0].email, color: '#d97706', bg: '#fffbeb' },
    { icon: <ClockCircleOutlined />, label: 'Business Hours', value: contactList[0].time, color: '#7c3aed', bg: '#f5f3ff' },
  ] : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Legal & Policies</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Contact Information</h2>
        </div>
        {contactList.length === 0 ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
            Add Contact
          </Button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<EditOutlined />} onClick={() => openModal(contactList[0])} style={{ borderRadius: 4, borderColor: '#2874f0', color: '#2874f0' }}>Edit</Button>
            <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(contactList[0]._id)} style={{ borderRadius: 4 }}>Delete</Button>
          </div>
        )}
      </div>

      {contactList.length > 0 ? (
        <Row gutter={[16, 16]}>
          {infoItems.map((item, i) => (
            <Col xs={24} sm={12} key={i}>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', padding: 20, display: 'flex', gap: 16, alignItems: 'flex-start', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color, fontSize: 20, flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{item.label}</div>
                  <div style={{ fontSize: 14, color: '#212121', fontWeight: 500, lineHeight: 1.5 }}>{item.value || '—'}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', padding: 60, textAlign: 'center' }}>
          <PhoneOutlined style={{ fontSize: 48, color: '#d1d5db', display: 'block', marginBottom: 12 }} />
          <div style={{ color: '#9ca3af', fontSize: 14 }}>No contact information added yet.</div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} style={{ marginTop: 16, background: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }}>
            Add Contact
          </Button>
        </div>
      )}

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Contact Information' : 'Add Contact Information'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={560}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="address" label="Address" rules={[{ required: true, message: 'Address is required' }]}>
            <TextArea rows={3} prefix={<EnvironmentOutlined />} placeholder="Enter full address" style={{ borderRadius: 4 }} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="number" label="Phone Number" rules={[{ required: true }]}>
              <Input prefix={<PhoneOutlined />} placeholder="Enter phone" size="large" />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
              <Input prefix={<MailOutlined />} placeholder="Enter email" size="large" />
            </Form.Item>
          </div>
          <Form.Item name="time" label="Business Hours" rules={[{ required: true }]}>
            <Input prefix={<ClockCircleOutlined />} placeholder="e.g. 09:00 AM - 10:00 PM" size="large" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Contact;